import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWifi, FaCloudUploadAlt, FaCheckCircle, FaClock } from 'react-icons/fa';
import useSyncStatus from '../hooks/useSyncStatus';
import db from '../utils/offlineDB';

/**
 * SyncStatusBanner — A compact, animated banner showing the farmer
 * their data's sync status. Renders below the header.
 *
 * States:
 *   offline  → amber bar with wifi-off icon
 *   pending  → amber bar with clock icon  ("5 items waiting to sync")
 *   syncing  → blue bar with animated upload icon
 *   synced   → green bar, auto-hides after 3 seconds
 */
const SyncStatusBanner = () => {
  const { syncState, pendingCount, isOnline } = useSyncStatus();
  const [showSynced, setShowSynced] = React.useState(false);

  // Show the "synced" banner briefly, then hide
  React.useEffect(() => {
    if (syncState === 'synced') {
      setShowSynced(true);
      const timer = setTimeout(() => setShowSynced(false), 3000);
      return () => clearTimeout(timer);
    } else {
      setShowSynced(false);
    }
  }, [syncState]);

  const shouldShow = syncState !== 'synced' || showSynced;

  const bannerConfig = {
    offline: {
      bg: 'bg-amber-600 dark:bg-amber-700',
      icon: <FaWifi className="text-white opacity-80" />,
      text: 'You are offline — viewing cached data',
    },
    pending: {
      bg: 'bg-amber-500 dark:bg-amber-600',
      icon: <FaClock className="text-white opacity-80" />,
      text: `${pendingCount} item${pendingCount !== 1 ? 's' : ''} waiting to sync`,
      action: async () => {
        if (window.confirm('Clear stuck sync items? This data will not be saved.')) {
          await db.mutationQueue.clear();
          window.dispatchEvent(new CustomEvent('outbox-updated'));
        }
      }
    },
    syncing: {
      bg: 'bg-blue-500 dark:bg-blue-600',
      icon: <FaCloudUploadAlt className="text-white animate-pulse" />,
      text: 'Syncing your data...',
    },
    synced: {
      bg: 'bg-emerald-500 dark:bg-emerald-600',
      icon: <FaCheckCircle className="text-white" />,
      text: 'All data synced',
    },
  };

  // If offline AND has pending items, show the offline message (takes priority)
  const activeState = !isOnline ? 'offline' : syncState;
  const config = bannerConfig[activeState] || bannerConfig.synced;

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className={`${config.bg} text-white text-sm font-medium sticky top-[60px] z-40 shadow-md overflow-hidden`}
        >
          <div className="flex items-center justify-center gap-2 px-4 py-2 relative">
            {config.icon}
            <span>{config.text}</span>
            {activeState === 'offline' && pendingCount > 0 && (
              <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {pendingCount} queued
              </span>
            )}
            
            {config.action && (
              <button 
                onClick={(e) => { e.stopPropagation(); config.action(); }}
                className="ml-4 bg-white/10 hover:bg-white/20 px-2 py-0.5 rounded border border-white/30 text-[10px] uppercase font-bold transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SyncStatusBanner;
